import { useState } from 'react';
import { DENOMINATIONS, MOVE_REASONS, countCash, type Counts } from '@njpos/core';
import { api, usd } from '../lib/api';
import { ErrorNote, Modal, useLoad } from './ui';

const money = (v: string) => v.replace(/[^\d.]/g, '');
const centsOf = (v: string) => Math.round(Number(v || '0') * 100);

/** Count the notes and coins in a drawer. Shows the total as you type. */
export function CountForm({ onSubmit, label = 'Close drawer', busy }: { onSubmit: (counts: Counts, otherCents: number, note: string) => void; label?: string; busy?: boolean }) {
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [other, setOther] = useState('');
  const [note, setNote] = useState('');
  const numeric: Counts = Object.fromEntries(Object.entries(counts).map(([k, v]) => [k, Number(v) || 0]));
  const total = countCash(numeric, centsOf(other));
  return (
    <div className="stack">
      <div className="count-grid">
        {DENOMINATIONS.map((d) => (
          <label key={d.key}>{d.label}<input inputMode="numeric" value={counts[d.key] ?? ''} onChange={(e) => setCounts({ ...counts, [d.key]: e.target.value.replace(/\D/g, '') })} placeholder="0" /></label>
        ))}
      </div>
      <label>Anything else counted (rolled coin, etc.), in dollars<input className="num-input" inputMode="decimal" value={other} onChange={(e) => setOther(money(e.target.value))} placeholder="0.00" /></label>
      <label>Note<input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" /></label>
      <div className="notice">Counted: <b>{usd(total)}</b></div>
      <button className="primary big" disabled={busy} onClick={() => onSubmit(numeric, centsOf(other), note)}>{label}</button>
    </div>
  );
}

/** The balance statement for one drawer: what should be there, what was counted, and the difference. */
export function DrawerStatement({ s }: { s: any }) {
  const f = s.flows;
  const rows: [string, number][] = [
    ['Opening cash', f.openingCents], ['Cash sales (net of change)', f.cashSalesCents], ['Lottery sales', f.lotterySalesCents], ['Lottery payouts', -f.lotteryPayoutsCents],
    ['Gift cards sold for cash', f.giftCardCashCents], ['Paid in', f.paidInCents], ['Paid out and safe drops', -f.paidOutCents],
  ];
  return (
    <div className="stack">
      <div className={`notice ${s.varianceCents === 0 ? 'good' : 'bad'}`}>
        {s.varianceCents === 0 ? 'Balanced. The drawer is exactly right.' : `${s.varianceCents < 0 ? 'Short' : 'Over'} by ${usd(Math.abs(s.varianceCents))}.`}
      </div>
      <table className="grid"><tbody>
        {rows.map(([l, c]) => <tr key={l}><td>{l}</td><td className="n">{usd(c)}</td></tr>)}
        <tr><td><b>Should be in the drawer</b></td><td className="n"><b>{usd(s.expectedCents)}</b></td></tr>
        <tr><td><b>Counted</b></td><td className="n"><b>{usd(s.countedCents)}</b></td></tr>
      </tbody></table>
      <div className="faint">{s.cashier} · {new Date(s.openedAt).toLocaleString()} to {new Date(s.closedAt).toLocaleString()} · {s.sales.count} sales{s.sales.refunds ? `, ${s.sales.refunds} refunds` : ''} · card {usd(s.tenders.card)}{s.tenders.gift ? ` · gift card ${usd(s.tenders.gift)}` : ''}</div>
      <button onClick={() => window.print()}>Print</button>
    </div>
  );
}

/** Paid in / paid out / safe drop. Reasons come from the shared list so the books are posted to the right account. */
export function MoveForm({ onDone }: { onDone: () => void }) {
  const [reason, setReason] = useState('safe_drop');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const m = MOVE_REASONS.find((x) => x.reason === reason)!;
  return (
    <div className="stack">
      <ErrorNote error={err} />
      <label>What for<select value={reason} onChange={(e) => setReason(e.target.value)}>
        <optgroup label="Cash out of the drawer">{MOVE_REASONS.filter((x) => x.kind === 'out').map((x) => <option key={x.reason} value={x.reason}>{x.label}</option>)}</optgroup>
        <optgroup label="Cash into the drawer">{MOVE_REASONS.filter((x) => x.kind === 'in').map((x) => <option key={x.reason} value={x.reason}>{x.label}</option>)}</optgroup>
      </select></label>
      <div className="field-row">
        <label>Amount<input autoFocus className="num-input" inputMode="decimal" value={amount} onChange={(e) => setAmount(money(e.target.value))} placeholder="0.00" /></label>
        <label>Note<input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. receipt #, who was paid" /></label>
      </div>
      <button className="primary" disabled={!(centsOf(amount) > 0)} onClick={async () => {
        setErr(null);
        try { await api('/api/cash-moves', { body: { kind: m.kind, reason, amountCents: centsOf(amount), note: note || undefined } }); onDone(); } catch (e) { setErr(e); }
      }}>Record</button>
    </div>
  );
}

/** The cashier's drawer at the register: open it with a starting amount, move cash, and balance it at the end of the shift. */
export function DrawerModal({ onClose }: { onClose: () => void }) {
  const d = useLoad(() => api<any>('/api/drawer'), []);
  const [mode, setMode] = useState<'home' | 'move' | 'close'>('home');
  const [opening, setOpening] = useState('');
  const [statement, setStatement] = useState<any>(null);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const s = d.data?.session;
  return (
    <Modal title="Cash drawer" onClose={onClose} wide>
      <ErrorNote error={err ?? d.error} />
      {statement ? (
        <><DrawerStatement s={statement} /><button className="primary big" onClick={onClose}>Done</button></>
      ) : !d.data ? <div className="empty">Loading…</div> : !s ? (
        <div className="stack">
          <p className="muted">Count the cash in the drawer and enter it to start your shift.</p>
          <label>Starting cash<input autoFocus className="num-input" inputMode="decimal" value={opening} onChange={(e) => setOpening(money(e.target.value))} placeholder="0.00" /></label>
          <button className="primary big" disabled={opening === '' || busy} onClick={async () => {
            setErr(null); setBusy(true);
            try { await api('/api/drawer/open', { body: { openingCents: centsOf(opening) } }); d.reload(); } catch (e) { setErr(e); } finally { setBusy(false); }
          }}>Open drawer</button>
        </div>
      ) : mode === 'move' ? (
        <div className="stack"><button onClick={() => setMode('home')}>Back</button><MoveForm onDone={() => { setMode('home'); d.reload(); }} /></div>
      ) : mode === 'close' ? (
        <div className="stack"><button onClick={() => setMode('home')}>Back</button>
          <p className="muted">Count everything in the drawer, including the starting cash.</p>
          <CountForm busy={busy} onSubmit={async (counts, otherCents, note) => {
            setErr(null); setBusy(true);
            try { setStatement(await api(`/api/drawer/${s.id}/close`, { body: { counts, otherCents, note: note || undefined } })); } catch (e) { setErr(e); } finally { setBusy(false); }
          }} />
        </div>
      ) : (
        <div className="stack">
          <div className="stats">
            <div><div className="v">{d.data.summary?.sales.count ?? 0}</div><div className="l">Sales this shift</div></div>
            <div><div className="v">{usd(d.data.summary?.tenders.cash ?? 0)}</div><div className="l">Cash taken</div></div>
            <div><div className="v">{usd(d.data.summary?.tenders.card ?? 0)}</div><div className="l">Card</div></div>
            {d.data.expectedCents !== undefined && <div><div className="v">{usd(d.data.expectedCents)}</div><div className="l">Should be in the drawer</div></div>}
          </div>
          <div className="faint">Opened {new Date(s.openedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} with {usd(s.openingCents)}</div>
          <div className="two" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button onClick={() => setMode('move')}>Paid in / out · safe drop</button>
            <button className="primary" onClick={() => setMode('close')}>Close drawer</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
